import { useCallback, useMemo } from 'react';
import { useQueries } from '@tanstack/react-query';
import { fetchBuildQuestions } from '../../api/authorWithAiApi';
import { getVideo } from '../../api/centerstageApi';
import { authorWithAiKeys } from '../../api/authorWithAiQueries';
import { OUTLINE_COPY, SELECTION_COPY } from './authorWithAiCopy';

/**
 * Labels for picked library items: question text from the Question Bank
 * (its search matches "Q<id>") and video titles from Centerstage, cached
 * by build and id. An item that no longer resolves shows its id with
 * "(no longer available)".
 */

export interface LibraryRef {
  kind: 'question' | 'video';
  libraryId: string;
}

export interface LibraryLabel {
  label: string;
  /** False while loading, on a transport error, or when the item is gone. */
  resolved: boolean;
}

const labelKey = (buildId: string, ref: LibraryRef) => [...authorWithAiKeys.build(buildId), 'label', ref.kind, ref.libraryId] as const;

async function resolveLabel(buildId: string, ref: LibraryRef): Promise<string | null> {
  if (ref.kind === 'video') return (await getVideo(ref.libraryId))?.title ?? null;
  const result = await fetchBuildQuestions(buildId, { search: `Q${ref.libraryId}`, pageSize: 25 });
  const item = result.items.find((candidate) => String(candidate.questionDefinitionId) === ref.libraryId);
  return item ? item.questionTextPlain : null;
}

const fallback = (ref: LibraryRef): string =>
  (ref.kind === 'video' ? SELECTION_COPY.videoFallback(ref.libraryId) : SELECTION_COPY.questionFallback(ref.libraryId));

export function useLibraryLabels(buildId: string, refs: readonly LibraryRef[]): (ref: LibraryRef) => LibraryLabel {
  const unique = useMemo(() => {
    const seen = new Map<string, LibraryRef>();
    for (const ref of refs) seen.set(`${ref.kind}:${ref.libraryId}`, ref);
    return [...seen.values()];
  }, [refs]);
  const results = useQueries({
    queries: unique.map((ref) => ({
      queryKey: labelKey(buildId, ref),
      queryFn: () => resolveLabel(buildId, ref),
      staleTime: 10 * 60 * 1000,
      retry: 1,
      placeholderData: undefined,
    })),
  });
  const byKey = useMemo(() => {
    const map = new Map<string, { data: string | null | undefined; isSuccess: boolean }>();
    unique.forEach((ref, index) => {
      const result = results[index];
      map.set(`${ref.kind}:${ref.libraryId}`, { data: result?.data, isSuccess: Boolean(result?.isSuccess) });
    });
    return map;
  }, [unique, results]);

  return useCallback((ref: LibraryRef): LibraryLabel => {
    const entry = byKey.get(`${ref.kind}:${ref.libraryId}`);
    if (entry?.isSuccess && entry.data) return { label: entry.data, resolved: true };
    if (entry?.isSuccess && entry.data === null) return { label: `${fallback(ref)} ${OUTLINE_COPY.noLongerAvailable}`, resolved: false };
    return { label: fallback(ref), resolved: false };
  }, [byKey]);
}
